import { PublicClip } from '@air/api';
import { ClipType, FormField, ResponseCustomFieldValue } from '@air/api/types';
import { InputFile } from '@air/component-upload-dropzone';
import { Upload } from '@air/redux-uploader';
import { extname } from 'path';

import { ACTIVE_UPLOAD_STATES, REJECTED_UPLOAD_STATES } from '~/store/uploader/constants';
import { getCustomFieldValue } from '~/utils/CustomFields';
import { containsRestrictedPathChars } from '~/utils/FileUtils';

export const getClipFormFieldValue = (clip: PublicClip, field: FormField) => {
  if (field.type === 'description') {
    return clip.description;
  } else if (field.type === 'title') {
    return clip.title;
  } else {
    return clip.customFields?.find((cf) => cf.id === field.customFieldId && getCustomFieldValue(cf));
  }
};

export const valueIfAllAreEqual = <T = ResponseCustomFieldValue | string | undefined | null>(
  values: T[],
): T | undefined => (values.every((val, i) => i === 0 || val === values[i - 1]) ? values[0] : undefined);

export const getBoardCollectionItems = ({
  uploads,
  clips,
}: {
  uploads: Upload[];
  clips: { [key: string]: PublicClip };
}) => {
  const uploadingClips = uploads.filter((upload) => ACTIVE_UPLOAD_STATES.includes(upload.status));

  const assets = Object.entries(clips)
    .filter(
      ([_id, clip]) =>
        clip.type !== ClipType.nonMedia &&
        // don't include assets that are currently uploading
        !uploads.find(
          (upload) =>
            upload.clipId === clip.id &&
            (ACTIVE_UPLOAD_STATES.includes(upload.status) || REJECTED_UPLOAD_STATES.includes(upload.status)),
        ),
    )
    .map(([_id, clip]) => clip);

  const files = Object.entries(clips)
    .filter(
      ([_id, clip]) =>
        clip.type === ClipType.nonMedia &&
        // don't include files that are currently uploading
        !uploads.find(
          (upload) =>
            upload.clipId === clip.id &&
            (ACTIVE_UPLOAD_STATES.includes(upload.status) || REJECTED_UPLOAD_STATES.includes(upload.status)),
        ),
    )
    .map(([_id, clip]) => clip);

  return {
    uploadingClips,
    assets,
    files,
  };
};

export const getSortedFilesAndMetadata = (
  acceptedFiles: InputFile[],
): {
  emptyFiles: InputFile[];
  filesWithNoExtension: InputFile[];
  filesWithRestrictedChars: InputFile[];
  files: InputFile[];
  totalSize: number;
} => {
  const emptyFiles: any[] = [];
  const filesWithNoExtension: any[] = [];
  const filesWithRestrictedChars: any[] = [];
  const files: any[] = [];
  let totalSize = 0;

  acceptedFiles.forEach((file) => {
    if (!file.size) {
      emptyFiles.push(file);
    } else if (!extname(file.name)) {
      filesWithNoExtension.push(file);
    } else if (containsRestrictedPathChars(file.name)) {
      filesWithRestrictedChars.push(file);
      totalSize += file.size;
    } else {
      files.push(file);
      totalSize += file.size;
    }
  });

  return {
    emptyFiles,
    files,
    totalSize,
    filesWithNoExtension,
    filesWithRestrictedChars,
  };
};
